import { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { ClerkProvider, SignIn, SignUp, Show, useClerk, useUser, useAuth } from "@clerk/react";
import { shadcn } from "@clerk/themes";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import SentryFallback from "@/components/SentryFallback";
import { setSentryUser, setSentrySchoolCode } from "@/lib/sentry";
import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Loader2 } from "lucide-react";
import NotFound from "@/pages/not-found";
import OAuthCallback from "@/pages/oauth-callback";

import Home from "@/pages/home";
import Onboarding from "@/pages/onboarding";
import Acknowledge from "@/pages/acknowledge";
import Pricing from "@/pages/pricing";
import Dashboard from "@/pages/app/dashboard";
import SubscriptionSuccess from "@/pages/app/subscription-success";
import SubscriptionCancel from "@/pages/app/subscription-cancel";
import Privacy from "@/pages/privacy";
import Terms from "@/pages/terms";
import Subprocessors from "@/pages/subprocessors";
import AiPrivacy from "@/pages/ai-privacy";
import AdminPage from "@/pages/app/admin";
import Diary from "@/pages/app/diary";
import Lessons from "@/pages/app/lessons";
import Notes from "@/pages/app/notes";
import Tasks from "@/pages/app/tasks";
import Courses from "@/pages/app/courses";
import Students from "@/pages/app/students";
import Classes from "@/pages/app/classes";
import Records from "@/pages/app/records";
import RecordNew from "@/pages/app/record-new";
import Analytics from "@/pages/app/analytics";
import SchoolOverview from "@/pages/app/school-overview";
import SchoolSetup from "@/pages/app/school-setup";
import Markbook from "@/pages/app/markbook";
import Exports from "@/pages/app/exports";
import Help from "@/pages/app/help";
import TimetableSettings from "@/pages/app/timetable-settings";
import Diaries from "@/pages/app/diaries";
import SettingsPage from "@/pages/app/settings";
import ProgramRegistrationDashboard from "@/pages/app/program-registration";
import ProgramRegistrationDetail from "@/pages/app/program-registration-detail";
import ArchivePage from "@/pages/app/archive";
import AppLayout from "@/components/layout/AppLayout";
import { DiaryProvider } from "@/contexts/DiaryContext";
import { RegionProvider } from "@/contexts/RegionContext";
import { AppearanceProvider, AppearanceServerSync, useAppearance } from "@/lib/appearance/provider";
import { ACCENT_BY_ID } from "@/lib/appearance/catalog";

import { useGetProfile } from "@workspace/api-client-react";

import { QueryCache, MutationCache } from "@tanstack/react-query";
import { ApiError } from "@workspace/api-client-react";
import { toast } from "@/hooks/use-toast";

/**
 * Surface 403 `subscriptionExpired` errors as a consistent toast no matter
 * which mutation triggered them. The persistent read-only banner in
 * AppLayout is the primary signal, but the toast confirms to the user
 * exactly which action was rejected so they understand why their save
 * silently did nothing.
 */
function handleApiError(err: unknown): void {
  if (err instanceof ApiError && err.status === 403) {
    const data = err.data as { subscriptionExpired?: boolean; error?: string } | null;
    if (data?.subscriptionExpired) {
      toast({
        title: "Subscribe to make changes",
        description:
          data.error ??
          "Your trial has ended. Your data is safe — subscribe to start saving changes again.",
        variant: "destructive",
      });
    }
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleApiError }),
  mutationCache: new MutationCache({ onError: handleApiError }),
});

// Fallback for local dev (where /api/config returns this same key anyway).
// In staging / production the runtime fetch below overwrites this with the
// key the API server was actually configured with, so the frontend and
// backend always share the same Clerk instance regardless of what was
// baked into the bundle at build time.
const _buildTimePubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;
// In dev, no proxy is active so leave undefined.
// In prod builds, compute dynamically from the current origin so it works on
// classtrack.com.au, the replit.app domain, and any future custom domain.
const clerkProxyUrl: string | undefined =
  import.meta.env.VITE_CLERK_PROXY_URL ||
  (import.meta.env.PROD ? `${window.location.origin}/api/__clerk` : undefined);
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}


// Clerk appearance keyed off the active accent so its surfaces follow
// the teacher's chosen palette.
function buildClerkAppearance(accentId: string) {
  const accent = ACCENT_BY_ID[accentId] ?? ACCENT_BY_ID.teal;
  return {
  theme: shadcn,
  cssLayerName: "clerk",
  options: {
    logoPlacement: "none" as const,
  },
  variables: {
    colorPrimary: `hsl(${accent.hsl})`,
    colorForeground: "hsl(222.2, 84%, 4.9%)",
    colorMutedForeground: "hsl(215.4, 16.3%, 46.9%)",
    colorDanger: "hsl(0, 84.2%, 60.2%)",
    colorBackground: "hsl(0, 0%, 100%)",
    colorInput: "hsl(0, 0%, 100%)",
    colorInputForeground: "hsl(222.2, 84%, 4.9%)",
    colorNeutral: "hsl(214.3, 31.8%, 91.4%)",
    fontFamily: "var(--font-sans)",
    borderRadius: "0.5rem",
  },
  elements: {
    rootBox: "w-full",
    cardBox: "bg-white rounded-2xl w-[440px] max-w-full overflow-hidden",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    header: "hidden",
    headerTitle: "hidden",
    headerSubtitle: "hidden",
    socialButtonsBlockButton: { display: "none" },
    dividerRow: { display: "none" },
    socialButtonsBlockButtonText: "font-semibold",
    formFieldLabel: "font-medium text-sm",
    footerActionLink: "text-primary hover:underline",
    footerActionText: "text-gray-500",
    dividerText: "text-xs text-gray-500 uppercase",
    identityPreviewEditButton: "text-primary hover:text-primary/80",
    formFieldSuccessText: "text-green-600",
    alertText: "text-sm",
    logoBox: "flex justify-center mb-4",
    logoImage: "h-10",
    formButtonPrimary: "bg-primary text-primary-foreground hover:bg-primary/90",
    formFieldInput: "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
    formFieldCheckboxInput: "w-4 h-4 rounded border border-input accent-primary cursor-pointer",
    formFieldCheckboxLabel: "text-sm font-medium leading-snug",
    formFieldCheckboxWrapper: "flex items-center gap-2",
    formButtonSecondary: "inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    footerAction: "flex justify-center space-x-1 mt-4 text-sm",
    dividerLine: "bg-gray-200",
    alert: "bg-red-50 text-red-600 border border-red-200 p-3 rounded-md",
    otpCodeFieldInput: "border-gray-200 rounded-md focus:border-primary focus:ring focus:ring-primary/20",
    formFieldRow: "mb-4",
    main: "px-8 py-6",
  },
  };
}

function useClerkAppearance() {
  const { prefs } = useAppearance();
  return useMemo(() => buildClerkAppearance(prefs.accent), [prefs.accent]);
}

function AuthBranding() {
  return (
    <div className="flex flex-col items-center mb-2">
      <div className="flex items-center gap-2 mb-1">
        <svg viewBox="0 0 32 32" className="h-8 w-8" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="32" height="32" rx="8" fill="#1cb08a"/>
          <path d="M8 11h10M8 16h14M8 21h8" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
          <circle cx="24" cy="21" r="4" fill="#5cd3ae" stroke="white" strokeWidth="1.5"/>
          <path d="M22.5 21l1 1 2-2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-2xl font-bold text-card-foreground tracking-tight">ClassTrack</span>
      </div>
      <p className="text-sm text-muted-foreground">Teacher planning &amp; compliance</p>
    </div>
  );
}

function SignInPage() {
  const clerkAppearance = useClerkAppearance();
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <div className="flex flex-col items-center">
        <AuthBranding />
        <SignIn routing="path" path={`${basePath}/sign-in`} signUpUrl={`${basePath}/sign-up`} appearance={clerkAppearance} />
      </div>
    </div>
  );
}

function SignUpPage() {
  const clerkAppearance = useClerkAppearance();
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <div className="flex flex-col items-center">
        <AuthBranding />
        <SignUp routing="path" path={`${basePath}/sign-up`} signInUrl={`${basePath}/sign-in`} appearance={clerkAppearance} />
      </div>
    </div>
  );
}

function ClerkAuthSetup() {
  const { getToken, userId } = useAuth();
  useEffect(() => {
    setAuthTokenGetter(() => getToken());
    return () => setAuthTokenGetter(null);
  }, [getToken]);
  useEffect(() => {
    setSentryUser(userId ?? null);
  }, [userId]);
  return null;
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);

  return null;
}

function HomeRedirect() {
  return (
    <>
      <Show when="signed-in">
        <Redirect to="/app/dashboard" />
      </Show>
      <Show when="signed-out">
        <Home />
      </Show>
    </>
  );
}

function AuthGuard({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useUser();
  const [location, setLocation] = useLocation();
  const { data: profile, isLoading, error } = useGetProfile({
    query: {
      enabled: isLoaded && isSignedIn,
      retry: false,
    }
  });

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      // Send unauthenticated visitors trying to access a protected /app/*
      // route to the sign-in page, not the public landing page.
      setLocation("/sign-in");
    }
  }, [isLoaded, isSignedIn, setLocation]);

  useEffect(() => {
    if (isLoaded && isSignedIn && !isLoading) {
      if (error) {
        // Assume 404 means no profile
        setLocation("/onboarding");
      } else if (profile && !profile.onboardingComplete && location !== "/onboarding") {
        setLocation("/onboarding");
      } else if (profile) {
        // Task #58 — gate every /app/* route on the current Privacy /
        // Terms versions being acknowledged. Version constants are
        // echoed by the server in the profile response so a stale tab
        // cannot bypass a policy bump.
        const needsAck =
          profile.privacyAcknowledgedVersion !== profile.currentPrivacyVersion ||
          profile.termsAcknowledgedVersion !== profile.currentTermsVersion;
        if (needsAck) {
          // Stash the originally requested route so /acknowledge can
          // return us there after a successful tick.
          if (typeof window !== "undefined") {
            const here = window.location.pathname + window.location.search;
            window.sessionStorage.setItem("ack:returnTo", here);
          }
          setLocation("/acknowledge");
        }
      }
    }
  }, [isLoaded, isSignedIn, isLoading, error, profile, setLocation]);

  useEffect(() => {
    setSentrySchoolCode(profile?.schoolCode ?? null);
  }, [profile?.schoolCode]);

  if (!isLoaded || (isSignedIn && isLoading)) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isSignedIn) return null;

  return <>{children}</>;
}

/**
 * SubscriptionGate is now a no-op. After Task #37 the app uses read-only
 * mode (see `useReadOnlyMode`) instead of locking lapsed users out behind a
 * paywall. The persistent banner in AppLayout and 403 responses from write
 * endpoints handle the affordance now.
 */
function SubscriptionGate({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <RegionProvider>
      <DiaryProvider>
        <AppLayout>
          <SubscriptionGate>
          <Switch>
            <Route path="/app/dashboard" component={Dashboard} />
            <Route path="/app/subscription/success" component={SubscriptionSuccess} />
            <Route path="/app/subscription/cancel" component={SubscriptionCancel} />
            <Route path="/app/admin" component={AdminPage} />
            <Route path="/app/diary" component={Diary} />
            <Route path="/app/lessons" component={Lessons} />
            <Route path="/app/notes" component={Notes} />
            <Route path="/app/tasks" component={Tasks} />
            <Route path="/app/courses" component={Courses} />
            <Route path="/app/students" component={Students} />
            <Route path="/app/classes" component={Classes} />
            <Route path="/app/records" component={Records} />
            <Route path="/app/records/new" component={RecordNew} />
            <Route path="/app/analytics" component={Analytics} />
            <Route path="/app/markbook" component={Markbook} />
            <Route path="/app/school-overview" component={SchoolOverview} />
            <Route path="/app/school-setup" component={SchoolSetup} />
            <Route path="/app/exports" component={Exports} />
            <Route path="/app/help" component={Help} />
            <Route path="/app/timetable-settings" component={TimetableSettings} />
            <Route path="/app/diaries" component={Diaries} />
            <Route path="/app/settings" component={SettingsPage} />
            <Route path="/app/program-registration/:id" component={ProgramRegistrationDetail} />
            <Route path="/app/program-registration" component={ProgramRegistrationDashboard} />
            <Route path="/app/archive" component={ArchivePage} />
            <Route component={NotFound} />
          </Switch>
          </SubscriptionGate>
        </AppLayout>
      </DiaryProvider>
    </RegionProvider>
  );
}

function OnboardingRoute() {
  return (
    <Show when="signed-in" fallback={<Redirect to="/sign-in" />}>
      <Onboarding />
    </Show>
  );
}

function AcknowledgeRoute() {
  return (
    <Show when="signed-in" fallback={<Redirect to="/sign-in" />}>
      <Acknowledge />
    </Show>
  );
}

function ClerkProviderWithRoutes({ clerkPubKey }: { clerkPubKey: string }) {
  const [, setLocation] = useLocation();
  const clerkAppearance = useClerkAppearance();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      signInFallbackRedirectUrl={`${basePath}/app/dashboard`}
      signUpFallbackRedirectUrl={`${basePath}/onboarding`}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkAuthSetup />
        <ClerkQueryClientCacheInvalidator />
        <AppearanceServerSync />
        <Switch>
          <Route path="/" component={HomeRedirect} />
          <Route path="/pricing" component={Pricing} />
          <Route path="/privacy" component={Privacy} />
          <Route path="/terms" component={Terms} />
          <Route path="/legal/subprocessors" component={Subprocessors} />
          <Route path="/subprocessors" component={Subprocessors} />
          <Route path="/ai-privacy" component={AiPrivacy} />
          <Route path="/safety/ai" component={AiPrivacy} />
          <Route path="/sign-in/*?" component={SignInPage} />
          <Route path="/sign-up/*?" component={SignUpPage} />
          <Route path="/v1/oauth_callback" component={OAuthCallback} />
          <Route path="/onboarding" component={OnboardingRoute} />
          <Route path="/acknowledge" component={AcknowledgeRoute} />
          {/* Bare /app (no sub-path) — redirect to dashboard so AuthGuard
              can run its profile / onboarding check normally.
              Wouter's "/*" wildcard requires a trailing "/" so "/app"
              would otherwise fall through to the NotFound catch-all. */}
          <Route path="/app">
            <Redirect to="/app/dashboard" />
          </Route>
          <Route path="/app/*">
            <AuthGuard>
              <AppRoutes />
            </AuthGuard>
          </Route>
          <Route component={NotFound} />
        </Switch>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

/**
 * Fetches the Clerk publishable key from /api/config before mounting
 * ClerkProvider.  This ensures the frontend always uses the same Clerk
 * instance as the API server, regardless of what VITE_CLERK_PUBLISHABLE_KEY
 * was baked into the bundle at build time.
 *
 * The fallback to _buildTimePubKey means local dev keeps working even
 * when /api/config is temporarily unavailable (e.g. first cold boot).
 */
function AppKeyLoader() {
  const [clerkPubKey, setClerkPubKey] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/config")
      .then((r) => r.json())
      .then((cfg: { clerkPublishableKey?: string }) => {
        setClerkPubKey(cfg.clerkPublishableKey || _buildTimePubKey);
      })
      .catch(() => {
        setClerkPubKey(_buildTimePubKey);
      });
  }, []);

  if (!clerkPubKey) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return <ClerkProviderWithRoutes clerkPubKey={clerkPubKey} />;
}

function App() {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <SentryFallback error={error} resetError={resetError} eventId={eventId} />
      )}
    >
      <TooltipProvider>
        <AppearanceProvider>
          <WouterRouter base={basePath}>
            <AppKeyLoader />
          </WouterRouter>
        </AppearanceProvider>
        <Toaster />
      </TooltipProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
